import { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { get, pick } from "lodash";
import styled from "@emotion/styled";

import {
    fetchDetailDevice,
    getActuatorCommand,
    updateActuatorCommand,
} from "service/gatewayService";
import "assets/css/event.css";
import {
    INFINITE,
    COUNTED,
    ACTUATOR_CONFIG_SUCCESS_MESSAGE,
    ACTUATOR_INVALID_MESSAGE,
    MaxActuatorLimit,
    MAX_LOOP_LIMIT,
    NUM_OF_LOOPS_ERROR_MESSAGE,
    TRY_AGAIN_ERROR_MESSAGE,
    DeactivateOption,
} from "constant";
import {
    isHttpSuccess,
    isFourWiredMotorControllerApp,
    getPrimaryUnitName,
    getPrimaryUnit,
} from "utils/functions";
import ModeOptions from "components/modals/ActuatorConfigurationModal/ModeOptions";
import { PolarityOptions } from "components/gateways/actuators/PolarityOptions";
import { RawDeactivateOptions } from "components/gateways/actuators/DeactivateOptions";
import MinuteSecondPicker from "components/common/MinuteSecondPicker";
import { RawActuatorCount } from "components/gateways/actuators/ActuatorCount";
import { RawCyclesInput } from "components/gateways/actuators/CyclesInput";
import { RawAnalogConfig } from "components/gateways/actuators/AnalogConfig";
import styledConst from "styles";
import {
    checkAllowLevelOnly,
    getActuatorConfigTemplate,
} from "utils/actuatorFunctions";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { Actuator, ActuatorCommandMode, ActuatorTiming } from "types/Actuator";
import { ResponseDto } from "types";
import { ActuatorCommandPatch } from "generated/models/Dashboard";

type CycleOption = typeof COUNTED | typeof INFINITE;

const defaultPairing: [number, number] = [0, 0];

const validatePulseMode = (timing?: ActuatorTiming) => {
    const t1 = get(timing, "[0][0]");
    const t2 = get(timing, "[0][1]");

    if (
        !t1 ||
        !t2 ||
        t1 <= 0 ||
        t2 <= 0 ||
        t1 > MaxActuatorLimit ||
        t2 > MaxActuatorLimit ||
        Number(t1 + t2) > MaxActuatorLimit
    ) {
        return ACTUATOR_INVALID_MESSAGE;
    }
    return null;
};

const validateLoopMode = (timing?: ActuatorTiming, cycle_count?: number) => {
    const t1 = get(timing, "[0][0]");
    const t2 = get(timing, "[0][1]");
    if (
        !t1 ||
        !t2 ||
        t1 <= 0 ||
        t2 <= 0 ||
        Number(t1 + t2) > MaxActuatorLimit
    ) {
        return ACTUATOR_INVALID_MESSAGE;
    }

    if (cycle_count && (cycle_count < 0 || cycle_count > MAX_LOOP_LIMIT)) {
        return NUM_OF_LOOPS_ERROR_MESSAGE;
    }

    return null;
};

const validateAnalogMode = (
    isAnalog?: boolean,
    activate_value?: number,
    deactivate_value?: number,
    minmax?: [number, number],
    unit?: string
) => {
    if (isAnalog && activate_value && deactivate_value && minmax && unit) {
        if (
            activate_value > Number(minmax[1]) ||
            activate_value < Number(minmax[0]) ||
            deactivate_value > Number(minmax[1]) ||
            deactivate_value < Number(minmax[0])
        ) {
            return `Unable to save. Please ensure Activation/Deactivation Voltage is between ${minmax[0]}${unit} and ${minmax[1]}${unit}.`;
        }
    }
    return null;
};

const validatePwmMode = (timing?: ActuatorTiming, cycle_count?: number) => {
    if (!timing || !Array.isArray(timing) || timing.length === 0) {
        return ACTUATOR_INVALID_MESSAGE;
    }

    for (const pair of timing) {
        const t1 = pair?.[0];
        const t2 = pair?.[1];

        if (
            !t1 ||
            !t2 ||
            t1 <= 0 ||
            t2 <= 0 ||
            Number(t1 + t2) > MaxActuatorLimit
        ) {
            return ACTUATOR_INVALID_MESSAGE;
        }
    }

    if (cycle_count && (cycle_count < 0 || cycle_count > MAX_LOOP_LIMIT)) {
        return NUM_OF_LOOPS_ERROR_MESSAGE;
    }

    return null;
};

const validateForm = ({
    mode,
    timing,
    cycle_count,
    isAnalog,
    activate_value,
    deactivate_value,
    minmax,
    unit,
}: Actuator & {
    minmax: [number, number];
    unit: string;
}) => {
    let errorMessage = null;

    if (mode === ActuatorCommandMode.PULSE) {
        errorMessage = validatePulseMode(timing);
    } else if (mode === ActuatorCommandMode.LOOP) {
        errorMessage = validateLoopMode(timing, cycle_count);
    } else if (mode === ActuatorCommandMode.PWM) {
        errorMessage = validatePwmMode(timing, cycle_count);
    }

    if (!errorMessage) {
        errorMessage = validateAnalogMode(
            isAnalog,
            activate_value,
            deactivate_value,
            minmax,
            unit
        );
    }

    return {
        hasError: !!errorMessage,
        message: errorMessage ?? "",
    };
};

const Container = styled.div`
    color: ${styledConst.Primary_Blue_3};
    font-size: 0.875rem;

    label {
        font-size: 1rem;
    }

    .polarity-selection label,
    input,
    select {
        font-size: 0.875rem;
    }

    .modal-footer {
        margin: 0 -25px -25px -25px;
    }

    .polarity-selection .custom-control-label {
        padding: 0;
        height: 134px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 11px;

        p {
            margin: 0 !important;
        }
    }
`;

const ActuatorConfigurationModal = ({
    gatewayId,
    ldsuId,
    said,
    onSuccess = () => { },
}: {
    gatewayId: string;
    ldsuId: string;
    said: string;
    onSuccess?: (actuatorCommand: Actuator) => void;
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isIOControllerState, setIsIOControllerState]: any = useState({
        isIOController: false,
        isAnalog: false,
        unit: "",
        minmax: [],
    });
    const [deviceInfo, setDeviceInfo] = useState<{
        detail: any;
        actuatorCommand: Actuator;
        mode: ActuatorCommandMode[];
        cycleOption: CycleOption;
        allowLevelOnly?: boolean;
    } | null>(null);

    const renderFormData = (mode: ActuatorCommandMode) => {
        if (!deviceInfo) {
            return;
        }
        const actuatorCommand = { ...deviceInfo.actuatorCommand };

        switch (mode) {
            case ActuatorCommandMode.PULSE:
                return (
                    <>
                        <MinuteSecondPicker
                            editedActuator={actuatorCommand}
                            onChange={(timing) =>
                                handleChangeFormData({ timing })
                            }
                            variant="light"
                        />
                        <RawDeactivateOptions
                            options={DeactivateOption.PULSE}
                            selectedOption={actuatorCommand?.deactivate}
                            onChange={(deactivate) =>
                                handleChangeFormData({ deactivate })
                            }
                        />
                    </>
                );
            case ActuatorCommandMode.LOOP:
                return (
                    <>
                        <MinuteSecondPicker
                            editedActuator={actuatorCommand}
                            onChange={(timing) =>
                                handleChangeFormData({ timing })
                            }
                            variant="light"
                        />

                        <Form.Label className="mt-3">
                            Number of loops
                        </Form.Label>
                        <RawActuatorCount
                            variant="light"
                            selectedLoopCycle={actuatorCommand?.cycle_count}
                            loopCycleOption={deviceInfo.cycleOption}
                            onChange={({ cycleOption, cycle_count }) =>
                                handleChangeFormData({
                                    cycleOption,
                                    cycle_count,
                                })
                            }
                        />
                        <RawDeactivateOptions
                            options={DeactivateOption.LOOP}
                            selectedOption={actuatorCommand?.deactivate}
                            onChange={(deactivate) =>
                                handleChangeFormData({ deactivate })
                            }
                        />
                    </>
                );
            case ActuatorCommandMode.PWM:
                if (
                    !actuatorCommand.timing ||
                    actuatorCommand.timing.length < 2
                ) {
                    actuatorCommand.timing = [defaultPairing, defaultPairing];
                }

                return (
                    <>
                        <RawCyclesInput
                            timing={actuatorCommand.timing}
                            ldsuId={ldsuId}
                            editedActuator={actuatorCommand}
                            onChange={(timing) =>
                                handleChangeFormData({ timing })
                            }
                            variant="light"
                        />
                        <Form.Label>Number of cycles</Form.Label>
                        <RawActuatorCount
                            variant="light"
                            selectedLoopCycle={actuatorCommand?.cycle_count}
                            loopCycleOption={deviceInfo.cycleOption}
                            onChange={({ cycleOption, cycle_count }) =>
                                handleChangeFormData({
                                    cycleOption,
                                    cycle_count,
                                })
                            }
                        />
                        <RawDeactivateOptions
                            options={DeactivateOption.PWM}
                            selectedOption={actuatorCommand?.deactivate}
                            onChange={(deactivate) =>
                                handleChangeFormData({ deactivate })
                            }
                        />
                    </>
                );

            // for IO controller
            default:
                // if digital output, return Level
                return <></>;
        }
    };

    const handleChangeFormData = (data: { [key: string]: any }) => {
        const extraData: Record<string, any> = {};
        // Reset "timing" when mode changes
        if (data.hasOwnProperty("mode")) {
            extraData.timing = undefined; // Must keep this to remove timing

            if (
                [ActuatorCommandMode.PULSE, ActuatorCommandMode.LOOP].includes(
                    data.mode
                )
            ) {
                extraData.timing = [defaultPairing];
            } else if (data.mode === ActuatorCommandMode.PWM) {
                extraData.timing = [defaultPairing, defaultPairing];
            }
        }

        if (data.hasOwnProperty("cycleOption")) {
            const cycleOption = data.cycleOption;
            if (cycleOption === INFINITE) {
                data.cycle_count = 0;
            } else {
                data.cycle_count = !Number.isNaN(Number(data.cycle_count))
                    ? Number(data.cycle_count)
                    : 0;
            }

            setDeviceInfo((prevState: any) => ({
                ...prevState,
                actuatorCommand: {
                    ...prevState.actuatorCommand,
                    ...extraData,
                    cycle_count: data.cycle_count,
                },
                cycleOption: data.cycleOption,
            }));
            return;
        }

        setDeviceInfo((prevState: any) => ({
            ...prevState,
            actuatorCommand: {
                ...prevState.actuatorCommand,
                ...data,
                ...extraData,
            },
        }));
    };

    const handleSubmit = async () => {
        if (!deviceInfo) {
            return;
        }

        setIsSubmitting(true);

        try {
            const { actuatorCommand } = deviceInfo;

            const formData: Actuator & {
                minmax: [number, number];
                unit: string;
            } = {
                name: actuatorCommand?.name,
                auto: actuatorCommand?.auto,
                mode: actuatorCommand?.mode,
                polarity: actuatorCommand?.polarity,
                timing: actuatorCommand?.timing,
                cycle_count: actuatorCommand?.cycle_count ?? 0,
                isAnalog: isIOControllerState.isAnalog,
                deactivate: actuatorCommand?.deactivate,
                activate_value: actuatorCommand?.activate_value,
                deactivate_value: actuatorCommand?.deactivate_value,
                minmax: isIOControllerState.minmax,
                unit: isIOControllerState.unit,
            };

            const validate = validateForm(formData);
            if (validate.hasError) {
                showErrorAlert({
                    message: validate.message,
                });
                return;
            }

            const body: Partial<Actuator> = getActuatorConfigTemplate(
                pick(formData, [
                    "name",
                    "auto",
                    "mode",
                    "polarity",
                    "timing",
                    "cycle_count",
                    "deactivate",
                    "activate_value",
                    "deactivate_value",
                ])
            );

            /**
             *  These fields are not allowed to update in this popup
             *  The "auto" is also not needed to configure here but must keep it in body to prevent it from being set
             *  to false by default (by API)
             */
            delete body.name;

            const saveConfigRes: ResponseDto<Actuator> =
                await updateActuatorCommand(
                    gatewayId,
                    ldsuId,
                    said,
                    body as ActuatorCommandPatch
                );

            if (isHttpSuccess(saveConfigRes.status) && saveConfigRes.data) {
                const { data } = saveConfigRes;

                setDeviceInfo((prevState: any) => ({
                    ...prevState,
                    actuatorCommand: data,
                }));

                showSuccessAlert({
                    message: ACTUATOR_CONFIG_SUCCESS_MESSAGE,
                });

                onSuccess?.(data);
            } else {
                showErrorAlert(
                    getAPIError(saveConfigRes, TRY_AGAIN_ERROR_MESSAGE)
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchActuator = async () => {
            const [actuatorRes, deviceRes] = await Promise.all([
                getActuatorCommand(gatewayId, ldsuId, said),
                fetchDetailDevice({
                    gateway_id: gatewayId,
                    device_id: ldsuId,
                    said,
                }),
            ]);

            // Check if only Level mode should be allowed (IOTP-8810)
            const allowLevelOnly = await checkAllowLevelOnly({
                isIOController: true,
                appId: deviceRes.APP[said].APPID,
                gatewayId,
                ldsuId,
            });

            // "4 Wire Motor" is not allowed to configure here
            const isFourWired = isFourWiredMotorControllerApp(
                deviceRes.OBJ,
                deviceRes.APP[said].APPID
            );

            if (!isHttpSuccess(actuatorRes.status) || isFourWired) {
                setDeviceInfo(null);
                return;
            }

            const {
                CLS: IOControllerCLS,
                MODE,
                APPLICATION,
            } = deviceRes.infoJson.SNS[said];

            const defaultUnitObj: any = getPrimaryUnitName(deviceRes.APP[said]);
            const unit = getPrimaryUnit(deviceRes.APP[said]);

            let mode = MODE && Array.isArray(MODE) && MODE;
            const application = APPLICATION?.find(
                (item: any) => item.APPID === deviceRes.APP[said].APPID
            );

            if (application?.MODE && Array.isArray(application?.MODE)) {
                mode = application.MODE;
            }

            // CLS = 32769 is Analog IO controller, otherwise digital IO controller
            if (IOControllerCLS === "32769") {
                setIsIOControllerState({
                    ...isIOControllerState,
                    isIOController: true,
                    isAnalog: true,
                    isGenericAnalogOutput:
                        deviceRes.type.toLowerCase() ===
                        "generic analog output",
                    unit: unit,
                    minmax: [defaultUnitObj.MIN, defaultUnitObj.MAX],
                    format: defaultUnitObj.FORMAT,
                    accuracy: defaultUnitObj.ACCURACY,
                    appId: deviceRes.APP[said].APPID,
                });
            } else {
                setIsIOControllerState({
                    ...isIOControllerState,
                    isIOController: true,
                    isAnalog: false,
                    appId: deviceRes.APP[said].APPID,
                    mode,
                });
            }

            setDeviceInfo({
                detail: deviceRes,
                actuatorCommand: actuatorRes.data,
                mode,
                cycleOption: actuatorRes.data.cycle_count ? COUNTED : INFINITE,
                allowLevelOnly,
            });
        };

        fetchActuator();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewayId, ldsuId, said]);

    if (!deviceInfo?.actuatorCommand) {
        return <div></div>;
    }

    return (
        <Container>
            <div>
                <Form>
                    <ModeOptions
                        mode={deviceInfo.actuatorCommand?.mode}
                        modeList={deviceInfo?.mode}
                        isIOController={true}
                        allowLevelOnly={!!deviceInfo?.allowLevelOnly}
                        onChange={(mode: string) =>
                            handleChangeFormData({
                                mode,
                            })
                        }
                    />
                </Form>

                {isIOControllerState.isAnalog ? (
                    <RawAnalogConfig
                        isIOControllerState={isIOControllerState}
                        value={deviceInfo.actuatorCommand?.value}
                        activate_value={
                            deviceInfo.actuatorCommand?.activate_value
                        }
                        deactivate_value={
                            deviceInfo.actuatorCommand?.deactivate_value
                        }
                        onChange={(field, value) =>
                            handleChangeFormData({ [field]: value })
                        }
                    />
                ) : (
                    <>
                        <Form.Label>Current Value</Form.Label>
                        <PolarityOptions
                            polarity={deviceInfo.actuatorCommand?.polarity}
                            mode={deviceInfo.actuatorCommand?.mode}
                            onChange={(polarity) =>
                                handleChangeFormData({ polarity })
                            }
                            variant="light"
                        />
                        {renderFormData(deviceInfo.actuatorCommand?.mode)}
                    </>
                )}
            </div>
            <div className="modal-footer">
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    Save
                </Button>
            </div>
        </Container>
    );
};

export default ActuatorConfigurationModal;
